import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        setPaymentData(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="booking-success-page" className="min-h-[80vh] flex items-center justify-center bg-[#F9F8F6] px-4 py-12">
      <div className="w-full max-w-md text-center">
        {status === "checking" && (
          <div>
            <div className="w-16 h-16 border-4 border-[#E68A00] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Processing Payment
            </h2>
            <p className="text-[#595959]">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="payment-success-heading">
              Booking Confirmed!
            </h2>
            <p className="text-[#595959] mb-6">Your payment was successful. Your equipment is reserved.</p>
            {paymentData?.metadata?.equipment_name && (
              <p className="text-sm text-[#595959] mb-6">Equipment: <strong>{paymentData.metadata.equipment_name}</strong></p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dashboard"
                data-testid="goto-dashboard-btn"
                className="bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-6 py-2.5 rounded-md transition-colors text-sm"
              >
                View My Rentals
              </Link>
              <Link
                to="/equipment"
                className="bg-[#1A2F24] hover:bg-[#264233] text-white font-semibold px-6 py-2.5 rounded-md transition-colors text-sm"
              >
                Browse More
              </Link>
            </div>
          </div>
        )}

        {(status === "timeout" || status === "expired") && (
          <div>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Payment Pending
            </h2>
            <p className="text-[#595959] mb-6">Your payment is still being processed. Please check your email for confirmation.</p>
            <Link to="/dashboard" className="bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-6 py-2.5 rounded-md transition-colors text-sm">
              View Dashboard
            </Link>
          </div>
        )}

        {status === "error" && (
          <div>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A2F24] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Something Went Wrong
            </h2>
            <p className="text-[#595959] mb-6">We couldn't verify your payment. Please contact us for help.</p>
            <Link to="/equipment" className="bg-[#E68A00] hover:bg-[#CC7A00] text-white font-semibold px-6 py-2.5 rounded-md transition-colors text-sm">
              Back to Equipment
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
